import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Search, Zap, Play, Pause, Settings, ChevronRight, Clock, Users, Bell,
  ArrowRightLeft, Phone, MapPin, MessageSquare, CheckCircle, RotateCcw,
  BookOpen, BarChart3, Shield, Target, Gift, Home, Tag, Sparkles,
  Download, AlertTriangle, Shuffle, RefreshCw, Heart, Calculator,
  FileText, Activity, Copy, GitBranch, PhoneCall, ClipboardList,
  CalendarCheck, BellRing, CalendarX, FileCheck, Hourglass, ShieldCheck,
  BadgePercent, HandMetal, Sprout, Building, ExternalLink
} from "lucide-react";

import { API } from "../config/api";

const ICON_MAP = {
  "message-circle": MessageSquare, "git-branch": GitBranch, "clock": Clock,
  "phone-call": PhoneCall, "copy": Copy, "alert-triangle": AlertTriangle,
  "phone-missed": Phone, "repeat": RefreshCw, "shuffle": Shuffle,
  "indian-rupee": BarChart3, "clipboard-list": ClipboardList, "tag": Tag,
  "sparkles": Sparkles, "arrow-right-left": ArrowRightLeft,
  "calendar-check": CalendarCheck, "bell-ring": BellRing, "users": Users,
  "calendar-x": CalendarX, "heart": Heart, "message-square": MessageSquare,
  "home": Home, "shield": Shield, "refresh-cw": RefreshCw, "bell": Bell,
  "file-text": FileText, "calculator": Calculator, "badge-percent": BadgePercent,
  "zap": Zap, "check-circle": CheckCircle, "file-check": FileCheck,
  "hand": HandMetal, "rotate-ccw": RotateCcw, "gift": Gift, "target": Target,
  "book-open": BookOpen, "trending-up": Activity, "party-popper": Sparkles,
  "building": Building, "hourglass": Hourglass, "shield-check": ShieldCheck,
  "bar-chart-3": BarChart3, "activity": Activity, "download": Download,
  "phone": Phone, "clipboard-check": ClipboardList, "map-pin": MapPin,
  "handshake": ArrowRightLeft, "sprout": Sprout, "settings": Settings,
};

const CAT_COLORS = {
  lead_capture: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", badge: "bg-emerald-100 text-emerald-800", iconBg: "bg-emerald-100" },
  first_contact: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", badge: "bg-blue-100 text-blue-800", iconBg: "bg-blue-100" },
  qualification: { bg: "bg-violet-50", border: "border-violet-200", text: "text-violet-700", badge: "bg-violet-100 text-violet-800", iconBg: "bg-violet-100" },
  site_visit: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", badge: "bg-amber-100 text-amber-800", iconBg: "bg-amber-100" },
  post_visit: { bg: "bg-teal-50", border: "border-teal-200", text: "text-teal-700", badge: "bg-teal-100 text-teal-800", iconBg: "bg-teal-100" },
  negotiation: { bg: "bg-orange-50", border: "border-orange-200", text: "text-orange-700", badge: "bg-orange-100 text-orange-800", iconBg: "bg-orange-100" },
  booking: { bg: "bg-green-50", border: "border-green-200", text: "text-green-700", badge: "bg-green-100 text-green-800", iconBg: "bg-green-100" },
  lost_lead: { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", badge: "bg-rose-100 text-rose-800", iconBg: "bg-rose-100" },
  nurture: { bg: "bg-lime-50", border: "border-lime-200", text: "text-lime-700", badge: "bg-lime-100 text-lime-800", iconBg: "bg-lime-100" },
  internal: { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-700", badge: "bg-slate-100 text-slate-800", iconBg: "bg-slate-100" },
};

const STATUS_STYLES = {
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  paused: "bg-amber-100 text-amber-800 border-amber-200",
  draft: "bg-slate-100 text-slate-600 border-slate-200",
  not_configured: "bg-white text-slate-400 border-slate-200",
};

function getIcon(name, className = "w-4 h-4") {
  const Icon = ICON_MAP[name] || Zap;
  return <Icon className={className} />;
}

// ============ TEMPLATE CARD ============
function TemplateCard({ template, catColor, onConfigure }) {
  const status = template.config_status || "not_configured";
  return (
    <Card
      className={`cursor-pointer hover:shadow-md transition-all duration-200 border ${catColor.border} group`}
      onClick={() => onConfigure(template)}
      data-testid={`template-card-${template.template_id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${catColor.iconBg} shrink-0`}>
            {getIcon(template.icon, `w-5 h-5 ${catColor.text}`)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-slate-800 font-['Manrope'] truncate">{template.name}</h3>
              <Badge variant="outline" className={`text-[10px] shrink-0 ${STATUS_STYLES[status]}`}>
                {status === "not_configured" ? "Not Set" : status}
              </Badge>
            </div>
            <p className="text-xs text-slate-500 mt-1 line-clamp-2">{template.description}</p>
            <div className="flex items-center gap-2 mt-2.5 flex-wrap">
              <Badge variant="secondary" className="text-[10px] gap-1">
                <Clock className="w-2.5 h-2.5" />{template.trigger?.description?.slice(0, 30)}
              </Badge>
              {template.channels?.slice(0, 2).map(ch => (
                <Badge key={ch} variant="outline" className="text-[10px] capitalize">{ch}</Badge>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="text-xs text-slate-400 flex items-center gap-1">
            Configure <ChevronRight className="w-3 h-3" />
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

// ============ CONFIG DIALOG ============
function ConfigDialog({ template, open, onClose, onSave, users, projects }) {
  const [config, setConfig] = useState({
    status: "draft",
    parameters: {},
    owner_type: "role",
    owner_id: "",
    priority: "medium",
    channels: [],
    delay: 0,
    delay_unit: "minutes",
    attached_stages: [],
    attached_campaigns: [],
    project_id: "all",
  });
  const [saving, setSaving] = useState(false);
  const [msgTemplates, setMsgTemplates] = useState([]);

  // Fetch approved message templates filtered by automation category
  useEffect(() => {
    if (template?.config_params?.some(p => p.type === "message_template_picker")) {
      const cat = template.category || "";
      axios.get(`${API}/message-templates/approved${cat ? `?category=${cat}` : ""}`).then(r => {
        // If category-filtered returns results, use them; otherwise fall back to all approved
        if (r.data.length > 0) {
          setMsgTemplates(r.data);
        } else {
          axios.get(`${API}/message-templates/approved`).then(r2 => setMsgTemplates(r2.data)).catch(() => {});
        }
      }).catch(() => {});
    }
  }, [template]);

  useEffect(() => {
    if (template) {
      const existing = template.config;
      if (existing) {
        setConfig({
          status: existing.status || "draft",
          parameters: existing.parameters || {},
          owner_type: existing.owner_type || template.owner_type || "role",
          owner_id: existing.owner_id || template.default_owner || "",
          priority: existing.priority || template.default_priority || "medium",
          channels: existing.channels?.length ? existing.channels : template.channels || [],
          delay: existing.delay ?? template.default_delay ?? 0,
          delay_unit: existing.delay_unit || template.delay_unit || "minutes",
          attached_stages: existing.attached_stages || [],
          attached_campaigns: existing.attached_campaigns || [],
          project_id: existing.project_id || "all",
        });
      } else {
        // Defaults from template
        const defaults = {};
        (template.config_params || []).forEach(p => {
          if (p.default !== undefined) defaults[p.key] = p.default;
        });
        setConfig({
          status: "draft",
          parameters: defaults,
          owner_type: template.owner_type || "role",
          owner_id: template.default_owner || "",
          priority: template.default_priority || "medium",
          channels: template.channels || [],
          delay: template.default_delay || 0,
          delay_unit: template.delay_unit || "minutes",
          attached_stages: [],
          attached_campaigns: [],
          project_id: "all",
        });
      }
    }
  }, [template]);

  const setParam = (key, val) => {
    setConfig(c => ({ ...c, parameters: { ...c.parameters, [key]: val } }));
  };

  const handleSave = async (status) => {
    setSaving(true);
    try {
      await onSave({ ...config, status, template_id: template.template_id });
    } finally {
      setSaving(false);
    }
  };

  if (!template) return null;

  const catColor = CAT_COLORS[template.category] || CAT_COLORS.internal;
  const STAGES = ["new", "contacted", "qualified", "site_visit_scheduled", "site_visit", "revisit", "negotiation_scheduled", "negotiation", "booked", "re_engaged", "lost"];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="config-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-xl ${catColor.iconBg}`}>
              {getIcon(template.icon, `w-6 h-6 ${catColor.text}`)}
            </div>
            <div>
              <DialogTitle className="font-['Manrope'] text-lg">{template.name}</DialogTitle>
              <p className="text-sm text-slate-500 mt-0.5">{template.description}</p>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-5 py-3">
          {/* Trigger & Actions Summary */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className={`p-3 rounded-lg ${catColor.bg} border ${catColor.border}`}>
              <p className="text-xs font-medium text-slate-500 mb-1">Trigger</p>
              <p className="text-sm font-medium text-slate-800">{template.trigger?.description}</p>
            </div>
            <div className={`p-3 rounded-lg ${catColor.bg} border ${catColor.border}`}>
              <p className="text-xs font-medium text-slate-500 mb-1">Actions ({template.actions?.length})</p>
              {template.actions?.map((a, i) => (
                <p key={i} className="text-xs text-slate-600 truncate">{a.description}</p>
              ))}
            </div>
          </div>

          {/* Configurable Parameters */}
          {template.config_params?.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-slate-700 font-['Manrope']">Parameters</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {template.config_params.map(param => (
                  <div key={param.key}>
                    <Label className="text-xs">{param.label}</Label>
                    {param.type === "boolean" ? (
                      <div className="flex items-center gap-2 mt-1">
                        <Switch
                          checked={config.parameters[param.key] ?? param.default ?? false}
                          onCheckedChange={v => setParam(param.key, v)}
                          data-testid={`param-${param.key}`}
                        />
                        <span className="text-xs text-slate-500">{config.parameters[param.key] ? "Enabled" : "Disabled"}</span>
                      </div>
                    ) : param.type === "select" ? (
                      <Select value={String(config.parameters[param.key] ?? param.default ?? "")} onValueChange={v => setParam(param.key, v)}>
                        <SelectTrigger className="mt-1 h-8 text-xs" data-testid={`param-${param.key}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {param.options?.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : param.type === "number" ? (
                      <Input
                        type="number"
                        className="mt-1 h-8 text-xs"
                        value={config.parameters[param.key] ?? param.default ?? 0}
                        onChange={e => setParam(param.key, parseInt(e.target.value) || 0)}
                        data-testid={`param-${param.key}`}
                      />
                    ) : param.type === "multi_select" ? (
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {param.options?.map(o => {
                          const vals = config.parameters[param.key] || param.default || [];
                          const active = vals.includes(o.value);
                          return (
                            <Badge
                              key={o.value}
                              variant={active ? "default" : "outline"}
                              className={`text-[10px] cursor-pointer ${active ? "" : "opacity-50"}`}
                              onClick={() => {
                                const newVals = active ? vals.filter(v => v !== o.value) : [...vals, o.value];
                                setParam(param.key, newVals);
                              }}
                            >
                              {o.label}
                            </Badge>
                          );
                        })}
                      </div>
                    ) : param.type === "role_or_user" ? (
                      <Select value={String(config.parameters[param.key] ?? param.default ?? "")} onValueChange={v => setParam(param.key, v)}>
                        <SelectTrigger className="mt-1 h-8 text-xs" data-testid={`param-${param.key}`}><SelectValue placeholder="Select role or user" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin (Role)</SelectItem>
                          <SelectItem value="sales_head">Sales Head (Role)</SelectItem>
                          <SelectItem value="sales_rep">Sales Rep (Role)</SelectItem>
                          <SelectItem value="telecaller">Telecaller (Role)</SelectItem>
                          {users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.role})</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : param.type === "message_template_picker" ? (
                      <div className="space-y-2 mt-1">
                        <Select value={String(config.parameters[param.key] ?? param.default ?? "")} onValueChange={v => setParam(param.key, v)}>
                          <SelectTrigger className="h-9 text-xs" data-testid={`param-${param.key}`}><SelectValue placeholder="Select message template" /></SelectTrigger>
                          <SelectContent>
                            {msgTemplates.length === 0 ? (
                              <div className="px-3 py-2 text-xs text-slate-400">No approved templates for this category.</div>
                            ) : (
                              <>
                                {["whatsapp", "sms", "email"].map(ch => {
                                  const chTemplates = msgTemplates.filter(mt => mt.channel === ch);
                                  if (chTemplates.length === 0) return null;
                                  return (
                                    <React.Fragment key={ch}>
                                      <div className="px-2 py-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider bg-slate-50">{ch}</div>
                                      {chTemplates.map(mt => (
                                        <SelectItem key={mt.template_id} value={mt.template_id}>
                                          <span className="flex items-center gap-1.5">
                                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${ch === "whatsapp" ? "bg-green-500" : ch === "sms" ? "bg-blue-500" : "bg-purple-500"}`} />
                                            {mt.name}
                                          </span>
                                        </SelectItem>
                                      ))}
                                    </React.Fragment>
                                  );
                                })}
                              </>
                            )}
                          </SelectContent>
                        </Select>
                        {(() => {
                          const selId = config.parameters[param.key] || param.default;
                          const sel = selId ? msgTemplates.find(m => m.template_id === selId) : null;
                          return sel ? (
                            <div className={`rounded-lg p-3 text-xs leading-relaxed whitespace-pre-wrap border ${
                              sel.channel === "whatsapp" ? "bg-green-50 border-green-200 text-green-900" :
                              sel.channel === "sms" ? "bg-blue-50 border-blue-200 text-blue-900" :
                              "bg-purple-50 border-purple-200 text-purple-900"
                            }`}>
                              {sel.subject && <p className="font-medium mb-1 text-[10px] opacity-70">Subject: {sel.subject}</p>}
                              {sel.content}
                              {sel.variables?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-current/10">
                                  {sel.variables.map(v => (
                                    <span key={v} className="px-1.5 py-0.5 rounded bg-white/60 text-[9px] font-mono">{`{${v}}`}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                          ) : null;
                        })()}
                        <a href="/message-templates" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-800 hover:underline">
                          <ExternalLink className="w-3 h-3" /> Manage Templates
                        </a>
                      </div>
                    ) : (
                      <Textarea
                        className="mt-1 text-xs min-h-[60px]"
                        value={config.parameters[param.key] ?? param.default ?? ""}
                        onChange={e => setParam(param.key, e.target.value)}
                        data-testid={`param-${param.key}`}
                      />
                    )}
                    {param.description && <p className="text-[10px] text-slate-400 mt-0.5">{param.description}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Project Scope */}
          <div>
            <Label className="text-xs font-medium flex items-center gap-1.5">
              <Building className="w-3.5 h-3.5 text-indigo-500" />
              Project Scope
            </Label>
            <Select value={config.project_id} onValueChange={v => setConfig(c => ({ ...c, project_id: v }))}>
              <SelectTrigger className="mt-1 h-8 text-xs" data-testid="project-scope-select">
                <SelectValue placeholder="Select project" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                    All Projects
                  </span>
                </SelectItem>
                {projects.map(p => (
                  <SelectItem key={p.project_id} value={p.project_id}>
                    <span className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-indigo-400" />
                      {p.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-slate-400 mt-0.5">
              {config.project_id === "all" ? "This automation will trigger for leads across all projects" : "This automation will only trigger for leads in the selected project"}
            </p>
          </div>

          {/* Owner & Priority */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Owner Type</Label>
              <Select value={config.owner_type} onValueChange={v => setConfig(c => ({ ...c, owner_type: v }))}>
                <SelectTrigger className="mt-1 h-8 text-xs" data-testid="owner-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="role">Role</SelectItem>
                  <SelectItem value="user">Specific User</SelectItem>
                  <SelectItem value="auto">Auto</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {config.owner_type === "user" ? (
              <div>
                <Label className="text-xs">Assign To</Label>
                <Select value={config.owner_id} onValueChange={v => setConfig(c => ({ ...c, owner_id: v }))}>
                  <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="Select user" /></SelectTrigger>
                  <SelectContent>
                    {users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : config.owner_type === "role" ? (
              <div>
                <Label className="text-xs">Assign To Role</Label>
                <Select value={config.owner_id} onValueChange={v => setConfig(c => ({ ...c, owner_id: v }))}>
                  <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="Select role" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="sales_head">Sales Head</SelectItem>
                    <SelectItem value="sales_rep">Sales Rep</SelectItem>
                    <SelectItem value="telecaller">Telecaller</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <div>
              <Label className="text-xs">Priority</Label>
              <Select value={config.priority} onValueChange={v => setConfig(c => ({ ...c, priority: v }))}>
                <SelectTrigger className="mt-1 h-8 text-xs" data-testid="priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Delay */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Delay</Label>
              <Input
                type="number"
                className="mt-1 h-8 text-xs"
                value={config.delay}
                onChange={e => setConfig(c => ({ ...c, delay: parseInt(e.target.value) || 0 }))}
                data-testid="delay-input"
              />
            </div>
            <div>
              <Label className="text-xs">Delay Unit</Label>
              <Select value={config.delay_unit} onValueChange={v => setConfig(c => ({ ...c, delay_unit: v }))}>
                <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="minutes">Minutes</SelectItem>
                  <SelectItem value="hours">Hours</SelectItem>
                  <SelectItem value="days">Days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Attach to Stages */}
          <div>
            <Label className="text-xs">Attach to Pipeline Stages (optional)</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {STAGES.map(s => {
                const active = config.attached_stages.includes(s);
                return (
                  <Badge
                    key={s}
                    variant={active ? "default" : "outline"}
                    className={`text-[10px] cursor-pointer capitalize ${active ? "" : "opacity-50"}`}
                    onClick={() => {
                      setConfig(c => ({
                        ...c,
                        attached_stages: active
                          ? c.attached_stages.filter(x => x !== s)
                          : [...c.attached_stages, s]
                      }));
                    }}
                  >
                    {s.replace(/_/g, " ")}
                  </Badge>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 flex-wrap">
          {template.config?.status === "active" && (
            <Button variant="outline" className="text-amber-600 border-amber-200 hover:bg-amber-50" onClick={() => handleSave("paused")} disabled={saving} data-testid="pause-btn">
              <Pause className="w-4 h-4 mr-1" />Pause
            </Button>
          )}
          <Button variant="outline" onClick={() => handleSave("draft")} disabled={saving} data-testid="save-draft-btn">
            Save as Draft
          </Button>
          <Button onClick={() => handleSave("active")} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="activate-btn">
            <Play className="w-4 h-4 mr-1" />{template.config?.status === "active" ? "Update & Keep Active" : "Activate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============ MAIN PAGE ============
export default function AutomationLibraryPage() {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({});
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [logStats, setLogStats] = useState({});
  const [logsLoading, setLogsLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [tplRes, catRes, statsRes, usersRes, logStatsRes, projRes] = await Promise.all([
        axios.get(`${API}/automations/templates`),
        axios.get(`${API}/automations/templates/categories`),
        axios.get(`${API}/automations/configs/stats`),
        axios.get(`${API}/auth/users`),
        axios.get(`${API}/automations/logs/stats`),
        axios.get(`${API}/projects`),
      ]);
      setTemplates(tplRes.data);
      setCategories(catRes.data);
      setStats(statsRes.data);
      setUsers(usersRes.data?.users || []);
      setLogStats(logStatsRes.data);
      setProjects(projRes.data || []);
    } catch (e) {
      toast.error("Failed to load automations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleConfigure = async (template) => {
    try {
      const res = await axios.get(`${API}/automations/templates/${template.template_id}`);
      setSelectedTemplate(res.data);
      setConfigOpen(true);
    } catch {
      toast.error("Failed to load template");
    }
  };

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API}/automations/logs?limit=50`),
        axios.get(`${API}/automations/logs/stats`),
      ]);
      setLogs(logsRes.data.logs || []);
      setLogStats(statsRes.data);
    } catch {
      toast.error("Failed to load logs");
    } finally {
      setLogsLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "logs" && logs.length === 0) fetchLogs();
  };

  const handleSave = async (configData) => {
    try {
      await axios.post(`${API}/automations/configs`, configData);
      toast.success(configData.status === "active" ? "Automation activated" : "Configuration saved");
      setConfigOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  const filtered = templates.filter(t => {
    if (activeTab !== "all" && activeTab !== "active" && t.category !== activeTab) return false;
    if (activeTab === "active" && t.config_status !== "active") return false;
    if (search) {
      const s = search.toLowerCase();
      return t.name.toLowerCase().includes(s) || t.description.toLowerCase().includes(s) || t.tags?.some(tg => tg.includes(s));
    }
    return true;
  });

  // Group by category for "all" view
  const grouped = {};
  if (activeTab === "all" || activeTab === "active") {
    filtered.forEach(t => {
      if (!grouped[t.category]) grouped[t.category] = [];
      grouped[t.category].push(t);
    });
  }

  return (
    <Layout>
      <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6" data-testid="automation-library-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-['Manrope'] text-slate-900">Automation Library</h1>
            <p className="text-sm text-slate-500 mt-1">Pre-built workflow automations for your sales pipeline</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={activeTab === "logs" ? "default" : "outline"}
              size="sm"
              className="text-xs gap-1.5"
              onClick={() => { handleTabChange("logs"); }}
              data-testid="view-logs-btn"
            >
              <Activity className="w-3.5 h-3.5" /> Execution Logs
            </Button>
          </div>
        </div>

        {/* Execution Stats Bar */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="automation-stats-bar">
          <Card className="border-emerald-200 bg-emerald-50/50 cursor-pointer hover:shadow-md transition-all" onClick={() => handleTabChange("active")} data-testid="stat-active">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-100"><Play className="w-4 h-4 text-emerald-600" /></div>
              <div>
                <p className="text-xl font-bold text-emerald-700">{stats.active || 0}</p>
                <p className="text-[10px] text-emerald-600">Active</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 cursor-pointer hover:shadow-md transition-all" onClick={() => handleTabChange("all")} data-testid="stat-templates">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-slate-100"><Zap className="w-4 h-4 text-slate-500" /></div>
              <div>
                <p className="text-xl font-bold text-slate-700">{stats.total_templates || 0}</p>
                <p className="text-[10px] text-slate-500">Templates</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-blue-200 bg-blue-50/50 cursor-pointer hover:shadow-md transition-all" onClick={() => handleTabChange("logs")} data-testid="stat-total-fires">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100"><Activity className="w-4 h-4 text-blue-600" /></div>
              <div>
                <p className="text-xl font-bold text-blue-700">{logStats.total_executions || 0}</p>
                <p className="text-[10px] text-blue-600">Total Fires</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-violet-200 bg-violet-50/50 cursor-pointer hover:shadow-md transition-all" onClick={() => handleTabChange("logs")} data-testid="stat-last-24h">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-violet-100"><Clock className="w-4 h-4 text-violet-600" /></div>
              <div>
                <p className="text-xl font-bold text-violet-700">{logStats.last_24h || 0}</p>
                <p className="text-[10px] text-violet-600">Last 24h</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-amber-200 bg-amber-50/50 cursor-pointer hover:shadow-md transition-all" onClick={() => handleTabChange("logs")} data-testid="stat-last-7d">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100"><BarChart3 className="w-4 h-4 text-amber-600" /></div>
              <div>
                <p className="text-xl font-bold text-amber-700">{logStats.last_7d || 0}</p>
                <p className="text-[10px] text-amber-600">Last 7 Days</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search automations..."
            className="pl-9 h-9 text-sm"
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-testid="automation-search"
          />
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="flex overflow-x-auto whitespace-nowrap h-auto p-1 bg-slate-100/80">
            <TabsTrigger value="all" className="text-xs px-3 py-1.5 shrink-0" data-testid="tab-all">
              All ({stats.total_templates || 0})
            </TabsTrigger>
            <TabsTrigger value="active" className="text-xs px-3 py-1.5 shrink-0" data-testid="tab-active">
              Active ({stats.active || 0})
            </TabsTrigger>
            {categories.map(cat => (
              <TabsTrigger key={cat.key} value={cat.key} className="text-xs px-3 py-1.5 shrink-0" data-testid={`tab-${cat.key}`}>
                {cat.label} ({cat.total})
              </TabsTrigger>
            ))}
            <TabsTrigger value="logs" className="text-xs px-3 py-1.5 shrink-0 text-slate-600" data-testid="tab-logs">
              Execution Logs
            </TabsTrigger>
          </TabsList>

          {/* All / Active Tab Content — grouped by category */}
          {(activeTab === "all" || activeTab === "active") && (
            <TabsContent value={activeTab} className="mt-4 space-y-6">
              {loading ? (
                <div className="flex items-center justify-center py-12 text-slate-400">Loading...</div>
              ) : Object.keys(grouped).length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                  <Zap className="w-10 h-10 mb-3 opacity-30" />
                  <p className="text-sm">{activeTab === "active" ? "No active automations yet" : "No templates found"}</p>
                </div>
              ) : (
                categories.filter(c => grouped[c.key]?.length > 0).map(cat => {
                  const catColor = CAT_COLORS[cat.key] || CAT_COLORS.internal;
                  return (
                    <div key={cat.key}>
                      <div className="flex items-center gap-2 mb-3">
                        <div className={`p-1.5 rounded-md ${catColor.iconBg}`}>
                          {getIcon(cat.icon, `w-4 h-4 ${catColor.text}`)}
                        </div>
                        <h2 className="text-base font-semibold font-['Manrope'] text-slate-800">{cat.label}</h2>
                        <span className="text-xs text-slate-400">{cat.description}</span>
                        {cat.active > 0 && <Badge className="text-[10px] bg-emerald-100 text-emerald-700">{cat.active} active</Badge>}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {grouped[cat.key].map(tpl => (
                          <TemplateCard key={tpl.template_id} template={tpl} catColor={catColor} onConfigure={handleConfigure} />
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
            </TabsContent>
          )}

          {/* Category-specific Tab Content */}
          {categories.map(cat => {
            const catColor = CAT_COLORS[cat.key] || CAT_COLORS.internal;
            return (
              <TabsContent key={cat.key} value={cat.key} className="mt-4">
                <div className={`p-4 rounded-xl ${catColor.bg} border ${catColor.border} mb-4`}>
                  <div className="flex items-center gap-2">
                    {getIcon(cat.icon, `w-5 h-5 ${catColor.text}`)}
                    <h2 className="text-base font-semibold font-['Manrope']">{cat.label}</h2>
                  </div>
                  <p className="text-xs text-slate-600 mt-1">{cat.description}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs">
                    <span className={catColor.text}>{cat.total} templates</span>
                    {cat.active > 0 && <Badge className="text-[10px] bg-emerald-100 text-emerald-700">{cat.active} active</Badge>}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {filtered.map(tpl => (
                    <TemplateCard key={tpl.template_id} template={tpl} catColor={catColor} onConfigure={handleConfigure} />
                  ))}
                </div>
              </TabsContent>
            );
          })}
          {/* Logs Tab */}
          <TabsContent value="logs" className="mt-4 space-y-4">
            {/* Top Templates */}
            {logStats.top_templates?.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-['Manrope']">Most Active Automations</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {logStats.top_templates.map(t => (
                      <Badge key={t.template_id} variant="secondary" className="text-xs gap-1">
                        {t.name} <span className="font-bold">({t.count})</span>
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Log Entries */}
            {logsLoading ? (
              <div className="flex items-center justify-center py-12 text-slate-400">Loading logs...</div>
            ) : logs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                <Activity className="w-10 h-10 mb-3 opacity-30" />
                <p className="text-sm">No automation executions yet</p>
                <p className="text-xs mt-1">Activate some automations to see logs here</p>
              </div>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="automation-logs-table">
                      <thead>
                        <tr className="border-b bg-slate-50/80">
                          <th className="text-left p-3 text-xs font-medium text-slate-500">Automation</th>
                          <th className="text-left p-3 text-xs font-medium text-slate-500">Lead</th>
                          <th className="text-left p-3 text-xs font-medium text-slate-500">Event</th>
                          <th className="text-left p-3 text-xs font-medium text-slate-500">Status</th>
                          <th className="text-left p-3 text-xs font-medium text-slate-500">Actions</th>
                          <th className="text-left p-3 text-xs font-medium text-slate-500">Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logs.map((log, i) => {
                          const catColor = CAT_COLORS[log.template_category] || CAT_COLORS.internal;
                          return (
                            <tr key={log.log_id || i} className="border-b last:border-0 hover:bg-slate-50/50">
                              <td className="p-3">
                                <div className="flex items-center gap-2">
                                  <div className={`p-1 rounded ${catColor.iconBg}`}>
                                    {getIcon(log.template_icon, `w-3 h-3 ${catColor.text}`)}
                                  </div>
                                  <div>
                                    <p className="text-xs font-medium text-slate-800 truncate max-w-[200px]">{log.template_name}</p>
                                    <p className="text-[10px] text-slate-400 capitalize">{log.template_category?.replace(/_/g, ' ')}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="p-3 text-xs text-slate-700">{log.lead_name || log.lead_id?.slice(0, 12)}</td>
                              <td className="p-3"><Badge variant="outline" className="text-[10px] capitalize">{log.event?.replace(/_/g, ' ')}</Badge></td>
                              <td className="p-3">
                                <Badge className={`text-[10px] ${log.status === "success" ? "bg-emerald-100 text-emerald-700" : log.status === "skipped" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                                  {log.status}
                                </Badge>
                              </td>
                              <td className="p-3 text-xs text-slate-500">
                                {log.actions_executed?.length || 0} action(s)
                              </td>
                              <td className="p-3 text-xs text-slate-400 whitespace-nowrap">
                                {log.executed_at ? new Date(log.executed_at).toLocaleString() : "-"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
            <div className="flex justify-center">
              <Button variant="outline" size="sm" onClick={fetchLogs} className="text-xs" data-testid="refresh-logs-btn">
                <RefreshCw className="w-3 h-3 mr-1" /> Refresh Logs
              </Button>
            </div>
          </TabsContent>
        </Tabs>

        {/* Config Dialog */}
        <ConfigDialog
          template={selectedTemplate}
          open={configOpen}
          onClose={() => setConfigOpen(false)}
          onSave={handleSave}
          users={users}
          projects={projects}
        />
      </div>
    </Layout>
  );
}
